import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { ArrowLeft, Send, MessageCircle, DollarSign, Briefcase, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Chat({ user, token }) {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (conversationId) {
      fetchConversation();
      // Poll for new messages every 3 seconds
      const interval = setInterval(fetchMessages, 3000);
      return () => clearInterval(interval);
    }
  }, [conversationId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchConversation = async () => {
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API}/conversations/${conversationId}`, config);
      setConversation(response.data.conversation);
      setMessages(response.data.messages);
    } catch (error) {
      toast.error('Error al cargar la conversación');
      navigate('/chats');
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API}/conversations/${conversationId}`, config);
      setMessages(response.data.messages);
    } catch (error) {
      console.log('Error fetching messages:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    setSending(true);
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.post(`${API}/conversations/${conversationId}/messages`, {
        conversation_id: conversationId,
        content: newMessage.trim()
      }, config);
      
      setNewMessage('');
      fetchMessages();
    } catch (error) {
      toast.error('Error al enviar mensaje');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <p className="text-slate-500">Cargando conversación...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col">
      {/* Header */}
      <div className="bg-[#3A4F6C] text-white p-4 sticky top-0 z-50">
        <div className="container mx-auto flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/chats')}
            className="text-white hover:bg-white/20"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          
          <div className="flex items-center gap-3 flex-1">
            <Avatar className="h-10 w-10 border-2 border-orange-400">
              <AvatarFallback className="bg-orange-500 text-white">
                {conversation?.other_party_name?.substring(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div>
              <h1 className="font-bold">{conversation?.other_party_name}</h1>
              <p className="text-xs text-slate-300">{conversation?.job_title}</p>
            </div>
          </div>

          <Badge className="bg-green-500">
            ${conversation?.accepted_price?.toLocaleString()}
          </Badge>
        </div>
      </div>

      {/* Job Info Banner */}
      <div className="bg-orange-50 border-b border-orange-200 p-3">
        <div className="container mx-auto flex items-center gap-2 text-sm">
          <Briefcase className="h-4 w-4 text-orange-600" />
          <span className="text-orange-800">
            <strong>Trabajo:</strong> {conversation?.job_title}
          </span>
          <span className="text-orange-600 ml-2">
            <DollarSign className="h-4 w-4 inline" />
            Precio acordado: ${conversation?.accepted_price?.toLocaleString()}
          </span>
          {conversation?.job_status === 'completed' && (
            <Badge className="bg-green-100 text-green-700 ml-auto">
              <CheckCircle className="h-3 w-3 mr-1" />
              Completado
            </Badge>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4" style={{ maxHeight: 'calc(100vh - 250px)' }}>
        <div className="container mx-auto max-w-3xl">
          {messages.map((msg) => {
            const isOwnMessage = msg.sender_id === user?.id;
            const isSystem = msg.sender_id === 'system';
            
            if (isSystem) {
              return (
                <div key={msg.id} className="flex justify-center my-4">
                  <div className="bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm text-center max-w-md">
                    <CheckCircle className="h-4 w-4 inline mr-1" />
                    {msg.content}
                  </div>
                </div>
              );
            }
            
            return (
              <div
                key={msg.id}
                className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[70%] rounded-2xl px-4 py-2 ${
                    isOwnMessage
                      ? 'bg-orange-500 text-white rounded-br-sm'
                      : 'bg-white text-slate-800 border border-slate-200 rounded-bl-sm'
                  }`}
                >
                  {!isOwnMessage && (
                    <p className="text-xs font-semibold text-orange-600 mb-1">
                      {msg.sender_name}
                    </p>
                  )}
                  <p className="break-words">{msg.content}</p>
                  <p className={`text-xs mt-1 ${isOwnMessage ? 'text-orange-200' : 'text-slate-400'}`}>
                    {new Date(msg.timestamp).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            );
          })}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Message Input */}
      <div className="bg-white border-t border-slate-200 p-4 sticky bottom-0">
        <div className="container mx-auto max-w-3xl">
          <form onSubmit={handleSendMessage} className="flex gap-2">
            <Input
              data-testid="message-input"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Escribe un mensaje..."
              className="flex-1"
              disabled={conversation?.status !== 'active'}
            />
            <Button
              data-testid="send-button"
              type="submit"
              disabled={!newMessage.trim() || sending || conversation?.status !== 'active'}
              className="bg-orange-500 hover:bg-orange-600 text-white px-6"
            >
              <Send className="h-5 w-5" />
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
